import { useFormik } from "formik";
import {
    Box,
    Typography,
    Button,
    Divider,
    Grid,
} from "@mui/material";
import { FC } from "react";
import { customizeValidationSchema } from "../variables/customSchema";
import useMutation from "../../../../../shared/components/hooks/useMutation";
import InputTextfield from "../../../../../shared/components/textField";
import { CustomDatePicker } from "../../../../../shared/components/datePicker";
import { formatAmountINR } from "../../../../../shared/utils/amountFormat";

interface CreatePaymentProps {
    feeTermID: string;
    studentClassID: string;
    feeTermName: string;
    feeTermAmount: number;
    onSuccess: () => void;
    onCancel: () => void;
}

const CreatePayment: FC<CreatePaymentProps> = ({
    feeTermID,
    studentClassID,
    feeTermName,
    feeTermAmount,
    onSuccess,
    onCancel,
}) => {
    const { modifyData, loading } = useMutation("/admin/payments");

    const paymentform = useFormik({
        initialValues: {
            paymentDate: new Date() as Date | null,
        },
        validationSchema: customizeValidationSchema,
        onSubmit: async (values) => {
            try {
                const payload = {
                    feeTermID: Number(feeTermID),
                    studentClassID: studentClassID,
                    paymentDate: values.paymentDate ? values.paymentDate.toISOString() : null,
                };
                await modifyData("/admin/payments", "post", payload);
                onSuccess();
            } catch (err) {
                console.error("Payment failed", err);
            }
        },
    });

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h5" fontWeight={600}>
                        Create Payment
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12}>
                    <Box
                        sx={{
                            p: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Confirm Payment Details
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            Term: <strong>{feeTermName}</strong>
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            Amount: <strong>{formatAmountINR(feeTermAmount)}</strong>
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} md={12}>
                    <Box sx={{ mt: 1 }}>
                        <CustomDatePicker
                            id="paymentDate"
                            name="paymentDate"
                            label="Payment Date"
                            value={paymentform.values.paymentDate}
                            onChange={(date: string | null) =>
                                paymentform.setFieldValue("paymentDate", date ? new Date(date) : null)
                            }
                            errorMessage={paymentform.touched?.paymentDate ? paymentform.errors?.paymentDate : undefined}
                            fullWidth
                            required
                        />
                    </Box>
                </Grid>


                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 2,
                            mt: 3,
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={onCancel}
                            sx={{ textTransform: "none" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            onClick={paymentform.submitForm}
                            disabled={loading}
                            sx={{
                                textTransform: "none",
                                minWidth: 150,
                                "&:hover": {
                                    backgroundColor: "primary.dark",
                                },
                            }}
                        >
                            {loading ? "Processing" : "Submit Payment"}
                        </Button>

                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CreatePayment;